'use client';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { GetNotesPageResponse } from '@elsevier-health/osmosis-shared/types/learndb/notes';
import NotesPageLeftSection from './NotesPageLeftSection';
import NotesPageRightSection from './NotesPageRightSection';
import { useEffect, useState } from 'react';
import NotesPageNotesBlock from './blocks/NotesPageNotesBlock';
import { useLearnAccess } from '../../../context/LearnAccessContext';
import { downloadNotes } from '../../../lib/notes/download';

interface NotesPageProps {
  notesPage: GetNotesPageResponse;
}

/**
 * NotesPage component displays the notes page.
 * @param {NotesPageProps} props - The notes data to display.
 * @throws {Error} Throws an error if no notes pages are found. Parent component should handle this error.
 */
const NotesPage = ({ notesPage }: NotesPageProps) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedPage, setSelectedPage] = useState(0);
  const { handleNotesAccess } = useLearnAccess();
  const pages = notesPage?.notes?.pages || [];
  const contents = notesPage?.notes?.contents || [];

  useEffect(() => {
    const page = window.location.hash.substring(1).replace('page-', '');
    if (page) scrollToPage(+page);
    // Notify Contents section on scroll
    const sections = document.querySelectorAll('[data-testid]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-testid');
            const page = id?.replace('page-', '');

            if (page && +page !== selectedPage) {
              setSelectedPage(+page);
            }
          }
        });
      },
      {
        root: null, // viewport
        threshold: 0.6, // 60% visible = active
      },
    );
    sections.forEach((section) => {
      if (section.getAttribute('data-testid')?.startsWith('page-')) {
        observer.observe(section);
      }
    });
  }, []); // Dont let him fool you, you don't need to add observers on each change of selectedPage

  useEffect(() => {
    scrollToPage(selectedPage);
  }, [isFullscreen]); // Scroll to selected page on fullscreen toggle, no need to scroll on selectedPage

  const scrollToPage = (page: number) => {
    const el = document.querySelector(`[data-testid="page-${page}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const handleDownload = async () => {
    const download = async () => {
      setIsDownloading(true);
      await downloadNotes(pages, notesPage.title);
      setIsDownloading(false);
    };

    if (notesPage.loggedOut) {
      handleNotesAccess(download);
    } else {
      await download();
    }
  };

  return (
    <Flex className={'flex-row p-4'}>
      <NotesPageLeftSection
        contents={contents}
        title={notesPage?.title}
        selectedPage={selectedPage}
        onSelectPage={scrollToPage}
        isFullscreen={isFullscreen}
        toggleFullscreen={toggleFullscreen}
        isDownloading={isDownloading}
        handleDownload={handleDownload}
      />
      <NotesPageNotesBlock
        title={notesPage?.title}
        pages={pages}
        isFullscreen={isFullscreen}
      />
      <NotesPageRightSection contents={contents} isFullscreen={isFullscreen} />
    </Flex>
  );
};

export default NotesPage;
